import React, { useState } from 'react';

import { DropdownButtons } from './_dropdown-buttons';
import { GroupButtons } from './_group-buttons';
import { BaseButton, Modal } from '@common/components';
import { Colors } from '@common/tokens';
import { createMessage } from 'works/legacy/lib/message-helper';
import { PerformWorkerTimesheetAction } from '@timekeeping/graphql';
import { WorkerTimesheetActions } from '@common/graphql/schema';
import { useMutation } from '@common/hooks';

const feedbackMap: Record<WorkerTimesheetActions, string> = {
  reject: 'rejected',
  approve: 'approved',
  unapprove: 'unapproved',
};

export const WorkerTimesheetActionButtons = ({
  timesheet,
  mode,
}: {
  mode: 'compact' | 'loose';
  timesheet: {
    id: string;
    canApprove: { value: boolean };
    canReject: { value: boolean };
    canUnapprove: { value: boolean };
    submittedAtTimesheet?: { pastApprovalDeadline: boolean } | null;
    status: string;
  };
}) => {
  const [performAction] = useMutation(PerformWorkerTimesheetAction, {
    refetchQueries: ['ManagerWorkerTimesheets', 'WorkerTimesheetStatusCounts'],
    onCompleted: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createMessage('success', `Timecard ${feedbackMap[currentAction!]}.`);
      setCurrentAction(undefined);
      setConfirmVisible(false);
    },
    onError: () => {
      createMessage(
        'error',
        `An error occurred while trying to ${currentAction} the timesheet`
      );
      setCurrentAction(undefined);
      setConfirmVisible(false);
    },
  });

  const [currentAction, setCurrentAction] = useState<
    WorkerTimesheetActions | undefined
  >();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const hasAnyPerm =
    timesheet.canApprove.value ||
    timesheet.canReject.value ||
    timesheet.canUnapprove.value;

  const onActionConfirm = (action: WorkerTimesheetActions) => {
    setCurrentAction(action);
    performAction({
      variables: {
        workerTimesheetId: timesheet.id,
        action,
      },
    });
  };

  const handleAction = (action: WorkerTimesheetActions) => {
    if (action === 'reject') {
      setConfirmVisible(true);

      return;
    }

    onActionConfirm(action);
  };

  if (!hasAnyPerm) {
    return null;
  }

  return (
    <>
      {mode === 'compact' ? (
        <DropdownButtons
          timesheet={timesheet}
          loading={currentAction}
          onAction={handleAction}
        />
      ) : (
        <GroupButtons
          timesheet={timesheet}
          loading={currentAction}
          onAction={handleAction}
        />
      )}
      <Modal
        visible={confirmVisible}
        title="Are you sure you want to reject?"
        onCancel={() => setConfirmVisible(false)}
        footer={
          <>
            <BaseButton
              type="ghost"
              className="worker-timesheet-reject-action-cancel"
              style={{ color: Colors.elm }}
              onClick={() => setConfirmVisible(false)}
              disabled={!!currentAction}
            >
              Cancel
            </BaseButton>
            <BaseButton
              type="ghost"
              className="worker-timesheet-reject-action-confirm"
              style={{ color: Colors.coral }}
              onClick={() => onActionConfirm('reject')}
              loading={!!currentAction}
            >
              Reject
            </BaseButton>
          </>
        }
      >
        This action cannot be undone.
      </Modal>
    </>
  );
};
